import type { Option, Turn } from "../api/types";
import { track } from "../analytics/posthog";
import { useI18n } from "../i18n";
import { asset } from "../config";

const optionValue = (o: Option): string =>
  o.value !== undefined && o.value !== null ? String(o.value) : o.text;

export const serviceName = (service?: string | null): string =>
  service === "WorkWell" ? "WorkWell" : "Connect to Work";

// Program-specific branding for the recommendation card. Logos live in
// public/logos; the accent colour (driven by the `match-card--<mod>` class in
// chat.css) is each program's own brand colour — WorkWell teal from its style
// sheet, Connect to Work violet from its wordmark.
const serviceBrand = (service?: string | null) =>
  service === "WorkWell"
    ? { mod: "workwell", logo: asset("logos/WorkWell-Logo.png"), alt: "WorkWell" }
    : { mod: "c2w", logo: asset("logos/connect-to-work-logo.jpg"), alt: "Connect to Work" };

// LLR local delivery pages (not the national NHS/gov.uk pages) — these are the
// services residents in Leicester, Leicestershire & Rutland are referred into.
const serviceWebsite = (service?: string | null): string =>
  service === "WorkWell"
    ? "https://leicesterleicestershireandrutlandhwp.uk/people-and-health/workwell-in-leicester-leicestershire-and-rutland/"
    : "https://www.leicestershire.gov.uk/business-and-consumers/supporting-businesses/about-connect-to-work";

type SelectHandler = (message: string, displayText: string) => void;

/** Safety intervention card (crisis). */
export function CrisisCard({ message }: { message: string }) {
  // NOT translated, deliberately. The crisis keyword guardrail matches English
  // only, so detection is already weaker in pa/gu; a fully localised crisis card
  // would imply a safety net that is not there. Matches the backend, where
  // CRISIS_EXIT stays English. See docs/specifications/MULTILINGUAL_TRANSLATION_SCOPE.md.
  return (
    <div className="crisis-warning-card" role="alert">
      <div className="crisis-warning-title">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        Safety Intervention
      </div>
      <div className="crisis-warning-body">{message}</div>
      <div className="crisis-helplines-grid">
        <a className="crisis-call-btn" href="tel:111" onClick={() => track("crisis_resource_clicked", { resource: "nhs_111", surface: "crisis_card" })}>
          <span className="crisis-call-btn__title">Call NHS 111</span>
          <p>Immediate 24/7 mental health help</p>
        </a>
        <a className="crisis-call-btn" href="tel:08088003302" onClick={() => track("crisis_resource_clicked", { resource: "mh_urgent_support", surface: "crisis_card" })}>
          <span className="crisis-call-btn__title">Call 0808 800 3302</span>
          <p>24/7 Mental Health Urgent Support</p>
        </a>
        <a className="crisis-call-btn" href="tel:116123" onClick={() => track("crisis_resource_clicked", { resource: "samaritans", surface: "crisis_card" })}>
          <span className="crisis-call-btn__title">Call Samaritans (116 123)</span>
          <p>Talk to a mental health volunteer</p>
        </a>
      </div>
      <p className="crisis-note">
        The referral triage session has been suspended for clinical safety. Please contact one of the
        services listed above or your local GP.
      </p>
    </div>
  );
}

/** Programme comparison tiles (options carry descriptions). */
export function ChoiceCard({
  turn,
  active,
  busy,
  onSelect,
}: {
  turn: Turn;
  active: boolean;
  busy: boolean;
  onSelect: SelectHandler;
}) {
  const { t } = useI18n();
  const options = turn.options ?? [];
  return (
    <>
      {turn.acknowledgment && (
        <div className="bot-message-text">
          <span className="bot-ack-inline">{turn.acknowledgment}</span>
        </div>
      )}
      <div className="bot-message-text">{turn.message}</div>
      <div className="choice-card-grid">
        {options.map((o) => (
          <div className="choice-tile" key={o.text}>
            <div className="choice-tile-name">{o.text}</div>
            {o.description && <div className="choice-tile-desc">{o.description}</div>}
            {typeof o.link === "string" && o.link.startsWith("http") && (
              <a className="choice-tile-link" href={o.link} target="_blank" rel="noopener noreferrer" onClick={() => track("resource_link_clicked", { link: "choice_tile", url: o.link })}>
                {t("card.findOutMore")}
              </a>
            )}
            <button
              type="button"
              className="choice-tile-btn"
              disabled={!active || busy}
              onClick={() => onSelect(optionValue(o), o.text)}
            >
              Choose {o.text}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

/** Recommendation + consent-to-proceed. "Yes" opens the referral form. */
export function MatchCard({
  turn,
  active,
  busy,
  onSelect,
  onProceed,
}: {
  turn: Turn;
  active: boolean;
  busy: boolean;
  onSelect: SelectHandler;
  onProceed: (service: string, proceed: Option) => void;
}) {
  const { t } = useI18n();
  const name = serviceName(turn.service_recommended);
  const brand = serviceBrand(turn.service_recommended);
  const recoText =
    (turn.preamble ?? []).find((p) => !/here to help|quick questions|stop or skip/i.test(p)) ?? "";
  const options = turn.options ?? [];
  // PROJ-114: a "switch" option means the resident is eligible for BOTH
  // programmes — show the pair card so the alternative is visible without
  // declining first. When it is absent, the single card is exactly as before.
  const switchOption = options.find((o) => o.value === "switch");

  if (switchOption) {
    return (
      <PairCard
        turn={turn}
        switchOption={switchOption}
        recoText={recoText}
        active={active}
        busy={busy}
        onSelect={onSelect}
        onProceed={onProceed}
      />
    );
  }

  return (
    <div className={`match-card match-card--${brand.mod}`}>
      <div className="match-card-badge">{t("card.goodFit")}</div>
      <div className="match-card-brand">
        <img className="match-card-logo" src={brand.logo} alt={brand.alt} />
      </div>
      {recoText && <div className="match-card-body">{recoText}</div>}
      <a className="match-card-link" href={serviceWebsite(turn.service_recommended)} target="_blank" rel="noopener noreferrer" onClick={() => track("resource_link_clicked", { link: "service_website", service: turn.service_recommended ?? "unknown" })}>
        {t("card.findOutAbout").replace("{service}", name)}
      </a>
      <div className="match-card-consent">{turn.message}</div>
      <div className="match-card-actions">
        {options.map((o) => {
          const val = optionValue(o);
          const isYes = o.value === true || val === "true";
          return (
            <button
              key={o.text}
              type="button"
              className="selection-option-btn"
              disabled={!active || busy}
              onClick={() =>
                isYes ? onProceed(turn.service_recommended ?? "", o) : onSelect(val, o.text)
              }
            >
              {o.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** PROJ-114 programme pair: the recommended programme leads, the alternative
 *  is fully visible and choosable — the resident never declines one to see the
 *  other. The consent question opens the card; each panel carries its own
 *  action (proceed → the referral form; switch → straight through); the
 *  decline stays reachable as a quiet action below the pair. */
function PairCard({
  turn,
  switchOption,
  recoText,
  active,
  busy,
  onSelect,
  onProceed,
}: {
  turn: Turn;
  switchOption: Option;
  recoText: string;
  active: boolean;
  busy: boolean;
  onSelect: SelectHandler;
  onProceed: (service: string, proceed: Option) => void;
}) {
  const { t } = useI18n();
  const options = turn.options ?? [];
  const isProceed = (o: Option) => o.value === true || optionValue(o) === "true";
  const proceedOption = options.find(isProceed);
  const declineOption = options.find((o) => !isProceed(o) && o.value !== "switch");
  const name = serviceName(turn.service_recommended);
  const brand = serviceBrand(turn.service_recommended);
  // collected_data.alternative_service names the other programme; the switch
  // option is only sent when a genuine alternative exists, so with exactly two
  // programmes the complement is a safe fallback.
  const altRaw = turn.collected_data?.alternative_service;
  const altService =
    altRaw === "WorkWell" || altRaw === "ConnectToWork"
      ? altRaw
      : turn.service_recommended === "WorkWell"
        ? "ConnectToWork"
        : "WorkWell";
  const altName = serviceName(altService);
  const altBrand = serviceBrand(altService);
  const disabled = !active || busy;

  return (
    <>
      {/* The consent question is a normal bot message, not part of the card —
          it reads as the assistant asking, with the two programmes below. */}
      {turn.message && <div className="bot-message-text">{turn.message}</div>}
      <div className={`match-card match-card--${brand.mod} match-card--pair`}>
      <div className="match-card-pair">
        <div className={`match-card-programme match-card-programme--${brand.mod} match-card-programme--lead`}>
          <div className="match-card-badge match-card-badge--pill">{t("card.goodFit")}</div>
          <div className="match-card-brand">
            <img className="match-card-logo" src={brand.logo} alt={brand.alt} />
          </div>
          {/* The personalised recommendation prose when this turn carried it;
              otherwise the programme's own description from the proceed
              option — a resumed turn has no preamble, and an empty panel
              inverted the hierarchy (the bug in the first implementation). */}
          {(recoText || proceedOption?.description) && (
            <div className="match-card-body">{recoText || proceedOption?.description}</div>
          )}
          <a className="match-card-link" href={serviceWebsite(turn.service_recommended)} target="_blank" rel="noopener noreferrer" onClick={() => track("resource_link_clicked", { link: "service_website", service: turn.service_recommended ?? "unknown" })}>
            {t("card.findOutAbout").replace("{service}", name)}
          </a>
          {proceedOption && (
            <button
              type="button"
              className="match-card-btn match-card-btn--primary"
              disabled={disabled}
              onClick={() => onProceed(turn.service_recommended ?? "", proceedOption)}
            >
              {proceedOption.text}
            </button>
          )}
        </div>
        <div className={`match-card-programme match-card-programme--${altBrand.mod}`}>
          <div className="match-card-badge">{t("card.anotherOption")}</div>
          <div className="match-card-brand">
            <img className="match-card-logo" src={altBrand.logo} alt={altBrand.alt} />
          </div>
          {switchOption.description && <div className="match-card-body">{switchOption.description}</div>}
          <a className="match-card-link" href={serviceWebsite(altService)} target="_blank" rel="noopener noreferrer" onClick={() => track("resource_link_clicked", { link: "service_website", service: altService })}>
            {t("card.findOutAbout").replace("{service}", altName)}
          </a>
          <button
            type="button"
            className="match-card-btn match-card-btn--secondary"
            disabled={disabled}
            onClick={() => onSelect(optionValue(switchOption), switchOption.text)}
          >
            {switchOption.text}
          </button>
        </div>
      </div>
      {declineOption && (
        <button
          type="button"
          className="match-card-decline"
          disabled={disabled}
          onClick={() => onSelect(optionValue(declineOption), declineOption.text)}
        >
          {declineOption.text}
        </button>
      )}
      </div>
    </>
  );
}

/** Terminal completion card. */
export function SummaryCard({ turn, reference }: { turn: Turn; reference?: string | null }) {
  const { t } = useI18n();
  const name = serviceName(turn.service_recommended);
  const brand = serviceBrand(turn.service_recommended);
  return (
    <div className={`triage-completion-card triage-completion-card--${brand.mod}`} role="status">
      {turn.service_recommended && (
        <div className="triage-completion-brand">
          <img className="triage-completion-logo" src={brand.logo} alt={brand.alt} />
        </div>
      )}
      <div className="triage-completion-body">{turn.message}</div>
      {turn.service_recommended && (
        <div className="triage-completion-badge">{t("card.referredTo").replace("{service}", name)}</div>
      )}
      {reference && (
        <div className="triage-completion-reference">
          <span className="triage-completion-reference-label">{t("card.referenceLabel")}</span>
          <span className="triage-completion-reference-code">{reference}</span>
          <span className="triage-completion-reference-hint">
            {t("card.referenceHint").replace("{service}", name)}
          </span>
        </div>
      )}
      <div className="triage-completion-disclaimer">
        {t("card.disclaimer").replace("{service}", name)}
      </div>
    </div>
  );
}
